/* ===========================================================================
   Browse - product deck interface

   Everything here talks to Shopify through the Section Rendering API
   (`?section_id=`), so the same code runs unchanged on a real store:

     /collections/{handle}?section_id=browse-deck&page=N&sort_by=S   a deck page
     /products/{handle}?section_id=browse-tile                       one tile
     /products/{handle}?section_id=browse-detail                     the detail card

   Favorites are handles in localStorage - a theme has no writable backend.
   Everything goes through the Favorites adapter below, so swapping in a
   customer-metafield or app backend later means rewriting only that object.
   =========================================================================== */

(function () {
  'use strict';

  var SECTIONS = {
    index: 'browse-index',
    detail: 'browse-detail'
  };

  var KEYS = {
    favorites: 'browse.favorites',
    activeTab: 'browse.activeTab',
    /* Where the deck was standing when a size request navigated away from it.
       sessionStorage, not local: it belongs to this visit and nothing else. */
    resume: 'browse.resume'
  };

  // -------------------------------------------------------------------------
  // Storage
  // -------------------------------------------------------------------------

  function readStore(store, key, fallback) {
    try {
      var raw = store.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeStore(store, key, value) {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* private mode / quota - favorites just won't persist */
    }
  }

  /**
   * The only place favorites are read or written.
   * Newest first, deduped, stored as product handles.
   */
  var Favorites = {
    list: function () {
      var value = readStore(window.localStorage, KEYS.favorites, []);
      return Array.isArray(value) ? value : [];
    },
    has: function (handle) {
      return this.list().indexOf(handle) !== -1;
    },
    add: function (handle) {
      var list = this.list();
      if (list.indexOf(handle) === -1) list.unshift(handle);
      writeStore(window.localStorage, KEYS.favorites, list);
      return list;
    },
    remove: function (handle) {
      var list = this.list().filter(function (h) { return h !== handle; });
      writeStore(window.localStorage, KEYS.favorites, list);
      return list;
    },
    toggle: function (handle) {
      var next = !this.has(handle);
      if (next) this.add(handle); else this.remove(handle);
      return next;
    },
    count: function () {
      return this.list().length;
    }
  };

  // -------------------------------------------------------------------------
  // Fetch helpers
  // -------------------------------------------------------------------------

  function fetchSection(url, sectionId) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status + ' for ' + url);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.getElementById('shopify-section-' + sectionId);
        return section ? section.innerHTML : html;
      });
  }

  /* Sizes come back from the Section Rendering API in US, the server default.
     If the shopper picked another unit, repaint whatever just arrived.
     Guarded, so a missing size-units.js costs the switcher, not the deck. */
  function applySizes(scope) {
    if (window.SizeUnits) window.SizeUnits.apply(scope);
  }

  var preloadCache = Object.create(null);

  function preload(src) {
    if (!src) return Promise.resolve();
    if (preloadCache[src]) return preloadCache[src];
    preloadCache[src] = new Promise(function (resolve) {
      var img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = src;
      if (img.complete) resolve();
    });
    return preloadCache[src];
  }

  // -------------------------------------------------------------------------
  // App
  // -------------------------------------------------------------------------

  var app = null;
  var collectionHandle = 'all';
  var panes = {};
  var activeTab = 'browse';

  /* Deck state (Browse pane only). `filters` is the drawer's selection, keyed
     by facet group; `results` is the catalogue with it applied.

     Plain numbered pages: page 2 is the same products every time it is asked
     for. The deck used to be a batch - it dropped everything already looked
     at, reshuffled, and rotated the sort each time it wrapped - which made
     "where was that pair" unanswerable. */
  var deck = {
    page: 1, perPage: 25, totalPages: 1,
    results: [],
    filters: {}
  };

  /* Every deck load takes a ticket. A chip clicked while a page is still in
     flight has to win, and the older response has to be dropped rather than
     painted over the newer one - which is also why there is no `busy` flag
     here any more: it turned a click during a load into nothing at all. */
  var deckToken = 0;

  function Pane(root) {
    this.root = root;
    this.key = root.getAttribute('data-pane-key');
    this.slot = root.querySelector('[data-deck-slot]');
    this.detail = root.querySelector('[data-detail-body]');
    this.count = root.querySelector('[data-deck-count]');
    this.pager = root.querySelector('[data-deck-pager]');
    this.loaded = false;
    this.dirty = false;
    this.currentHandle = null;
    /* The pair a shopper chose, as opposed to the one the deck opened for them.
       Only this one reaches the address bar - see openProduct. */
    this.pickedHandle = null;
  }

  Pane.prototype.tiles = function () {
    return Array.prototype.slice.call(this.slot.querySelectorAll('[data-tile]'));
  };

  Pane.prototype.tile = function (handle) {
    return this.slot.querySelector('[data-tile][data-handle="' + cssEscape(handle) + '"]');
  };

  Pane.prototype.setCount = function (text) {
    if (this.count) this.count.textContent = text;
  };

  Pane.prototype.setLoading = function () {
    this.slot.innerHTML = '<p class="pane-loading">Loading&hellip;</p>';
  };

  Pane.prototype.setEmpty = function (message) {
    this.slot.innerHTML = '<p class="pane-empty">' + message + '</p>';
    this.detail.innerHTML = '<div class="pane-empty">No product selected</div>';
    this.currentHandle = null;
  };

  function cssEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  // -------------------------------------------------------------------------
  // Tabs
  // -------------------------------------------------------------------------

  function showTab(name, options) {
    if (!panes[name]) name = 'browse';
    activeTab = name;

    Array.prototype.forEach.call(app.querySelectorAll('[data-pane]'), function (pane) {
      pane.hidden = pane.getAttribute('data-pane') !== name;
    });
    /* Document-scoped: the tabs render in the top bar section, outside
       [data-browse-app]. */
    Array.prototype.forEach.call(document.querySelectorAll('.tab'), function (button) {
      var isActive = button.getAttribute('data-tab') === name;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (!options || !options.skipSave) {
      try { window.localStorage.setItem(KEYS.activeTab, name); } catch (error) {}
    }

    var pane = panes[name];
    if (!pane.loaded || pane.dirty) loadPane(pane);
  }

  /* Nothing renders before the catalogue is in - and the pane is not marked
     loaded until it does, or the tab would never fill itself in afterwards. */
  function loadPane(pane, options) {
    if (!Catalog.ready) return;
    pane.dirty = false;
    pane.loaded = true;
    return pane.key === 'favorites' ? renderFavorites(pane) : renderDeck(pane, options);
  }

  // -------------------------------------------------------------------------
  // Catalog
  //
  // The whole collection, once, as JSON - through sections/browse-index.liquid,
  // which carries the reasoning for why the deck loads a catalogue instead of
  // asking Shopify for a filtered page. Short version: gender, colour and style
  // are all tags, Shopify ORs the values of one filter and ANDs across
  // different ones, so there is no arrangement of `filter.p.tag` that gives
  // faceted search over a single tag namespace.
  // -------------------------------------------------------------------------

  var Catalog = {
    items: [],
    ready: false,
    failed: false,
    promise: null,

    load: function () {
      if (this.promise) return this.promise;
      var self = this;

      this.promise = this.page(1)
        .then(function (first) {
          /* 250 a page is Shopify's ceiling. One request today; the rest are
             asked for together rather than in series, so a catalogue that
             outgrows one page costs latency once and not per page. */
          var rest = [];
          for (var n = 2; n <= (first.pages || 1); n += 1) rest.push(self.page(n));
          return Promise.all(rest).then(function (chunks) {
            var items = (first.items || []).slice();
            chunks.forEach(function (chunk) {
              items = items.concat(chunk.items || []);
            });
            return items;
          });
        })
        .then(function (items) {
          self.items = items;
          items.forEach(Facets.classify);
          Facets.index(items);
          self.ready = true;
          document.dispatchEvent(new CustomEvent('browse:catalog'));
          return items;
        })
        .catch(function (error) {
          console.error('[browse] catalog load failed', error);
          self.failed = true;
          self.ready = true;
          document.dispatchEvent(new CustomEvent('browse:catalog'));
          return [];
        });

      return this.promise;
    },

    page: function (number) {
      var url = '/collections/' + encodeURIComponent(collectionHandle) +
        '?section_id=' + SECTIONS.index + '&page=' + number;

      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status + ' for ' + url);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var payload = doc.querySelector('script[data-browse-index]');
          if (!payload) throw new Error('no index payload in ' + url);
          /* textContent, not innerHTML: re-serialising would turn the & in a
             product title into &amp; and break the parse. */
          return JSON.parse(payload.textContent);
        });
    },

    byHandle: function (handle) {
      for (var i = 0; i < this.items.length; i += 1) {
        if (this.items[i].h === handle) return this.items[i];
      }
      return null;
    }
  };

  // -------------------------------------------------------------------------
  // Facets
  //
  // A tag is not a facet. "Men", "mens" and "men's shoes" are one filter to a
  // shopper and three strings to a store, so every tag is normalised into a
  // label first and the filter matches on the label. Whatever the store was
  // tagged with, the panel shows one row for it - and anything that fits no
  // group is still offered under Other rather than quietly dropped, which is
  // how a filter panel ends up not matching the catalogue behind it.
  // -------------------------------------------------------------------------

  /* US is the pivot: every size on a card is read across from it, and the
     size facet is keyed by it. A store with no `custom.us_size` metafield
     hands us only the size as recorded, so convert that once here rather than
     leaving the whole size column blank. Same chart the cards paint with, so
     a tile and its detail card can never disagree. */
  function resolveSize(item) {
    if (item.us || !item.nv || !window.SizeUnits) return;

    /* The recorded unit is checked against the chart before it is trusted, and
       the magnitude is the fallback when it is not a unit at all. A store whose
       variant titles are "8 / DarkGreen" recorded its unit as "/DARKGREEN";
       taking that at face value meant every size failed to convert and the Size
       facet disappeared from the drawer on a store that has a size for every
       single product. `paint()` already worked this way - this did not. */
    var unit = window.SizeUnits.normaliseUnit(item.nu) ||
      window.SizeUnits.inferUnit(item.nv);
    var us = unit ? window.SizeUnits.toUS(item.nv, unit, item.ch) : null;
    if (us) item.us = us;
  }

  /* Size is a scale, not a list.
     Every size the catalogue has ever held stays on offer at zero rather than
     disappearing when another filter rules it out, so the grid keeps its shape:
     a shopper's own size stays in the same place and simply goes dim, which is
     the answer to "do they have mine". A grid that reflows under the cursor as
     you tick things is a grid you cannot aim at.
     Every other group drops its empties, where a row matching nothing is noise
     rather than information. */
  var SCALE_GROUPS = { size: true };

  var COLOR_WORDS = [
    'black', 'brown', 'burgundy', 'green', 'multicolor', 'multicolour', 'white',
    'blue', 'navy', 'red', 'silver', 'beige', 'gold', 'tan', 'orange', 'purple',
    'grey', 'gray', 'cream', 'pink', 'yellow', 'bronze', 'ivory', 'khaki',
    'olive', 'charcoal'
  ];

  var GENDER_RULES = [
    [/^(wom[ae]n|womens|wom[ae]n's)( shoes)?$/, 'Women'],
    [/^(m[ae]n|mens|m[ae]n's)( shoes)?$/, 'Men'],
    [/^(kid|kids|youth|child|children|children's)( shoes)?$/, 'Kids'],
    [/^unisex( shoes)?$/, 'Unisex']
  ];

  var GENDER_ORDER = ['Men', 'Women', 'Unisex', 'Kids'];
  var AVAILABILITY_ORDER = ['In stock', 'Sold'];

  function normalise(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/’/g, "'");
  }

  function pushUnique(list, value) {
    if (value && list.indexOf(value) === -1) list.push(value);
  }

  function genderOf(low) {
    for (var i = 0; i < GENDER_RULES.length; i += 1) {
      if (GENDER_RULES[i][0].test(low)) return GENDER_RULES[i][1];
    }
    return null;
  }

  var Facets = {
    /** Order is the order they appear in the panel: size first, for a store of one-offs. */
    GROUPS: [
      { key: 'size', label: 'Size' },
      { key: 'gender', label: 'Department' },
      { key: 'style', label: 'Style' },
      { key: 'color', label: 'Color' },
      { key: 'availability', label: 'Availability' },
      { key: 'tags', label: 'Other' }
    ],

    /** Sort every tag on one product into its group. Called once per product. */
    classify: function (item) {
      var gender = [];
      var color = [];
      var style = [];
      var other = [];

      (item.tg || []).forEach(function (raw) {
        var low = normalise(raw);
        if (!low) return;
        if (low.indexOf(':') !== -1) return;      // Shopify's own namespaced tags
        if (/^size[-_ ]/.test(low)) return;       // size is read off the variant, not a tag

        var found = genderOf(low);
        if (found) { pushUnique(gender, found); return; }
        if (COLOR_WORDS.indexOf(low) !== -1) { pushUnique(color, titleCase(low)); return; }
        pushUnique(other, String(raw).trim());
      });

      /* Product type is the store's own word for style, and the colour
         metafield is its own word for colour. Where they are set they win, and
         the tags are what fills the group in when they are not. */
      if (item.ty) pushUnique(style, String(item.ty).trim());
      if (item.c) pushUnique(color, titleCase(String(item.c).trim()));

      if (!style.length) {
        style = other;
        other = [];
      } else {
        var known = style.map(normalise);
        other = other.filter(function (tag) { return known.indexOf(normalise(tag)) === -1; });
      }

      /* Men's US 9 and women's US 9 are different shoes, so the chart is part
         of the key. The label sorts that out for the shopper. */
      resolveSize(item);

      item.f = {
        size: item.us ? [(item.ch || 'men') + '|' + item.us] : [],
        gender: gender,
        style: style,
        color: color,
        availability: [item.a ? 'In stock' : 'Sold'],
        tags: other
      };
    },

    /**
     * Values carried by every single product. A brand tag on all 128 pairs is
     * a true fact and a useless filter - it narrows nothing, and at the top of
     * a list sorted by count it is the first thing a shopper tries. Indexed
     * once, after classify, and skipped when the options are built.
     */
    universal: {},

    /* Every value a SCALE_GROUPS group has ever held, so options() can seed the
       run at zero instead of letting it shrink. */
    scale: {},

    index: function (items) {
      var totals = {};
      Facets.scale = {};
      items.forEach(function (item) {
        Facets.GROUPS.forEach(function (group) {
          ((item.f && item.f[group.key]) || []).forEach(function (value) {
            var id = group.key + ' ' + value;
            totals[id] = (totals[id] || 0) + 1;
            if (SCALE_GROUPS[group.key]) Facets.scale[group.key + ' ' + value] = value;
          });
        });
      });

      Facets.universal = {};
      if (items.length < 2) return;
      Object.keys(totals).forEach(function (id) {
        if (totals[id] === items.length) Facets.universal[id] = true;
      });
    },

    /** AND across groups, OR within one - which is what a shopper means. */
    matches: function (item, state) {
      for (var key in state) {
        if (!Object.prototype.hasOwnProperty.call(state, key)) continue;
        var picked = state[key];
        if (!picked || !picked.length) continue;
        var have = (item.f && item.f[key]) || [];
        var hit = false;
        for (var i = 0; i < picked.length; i += 1) {
          if (have.indexOf(picked[i]) !== -1) { hit = true; break; }
        }
        if (!hit) return false;
      }
      return true;
    },

    filter: function (state) {
      return Catalog.items.filter(function (item) {
        return Facets.matches(item, state || {});
      });
    },

    /**
     * The options for one group, counted against every OTHER group's
     * selection - so a count reads as "this many more if you also pick this",
     * and picking a second colour never shows 0 beside the first.
     */
    options: function (key, state) {
      var without = {};
      for (var other in state) {
        if (Object.prototype.hasOwnProperty.call(state, other) && other !== key) {
          without[other] = state[other];
        }
      }

      var counts = {};
      Catalog.items.forEach(function (item) {
        if (!Facets.matches(item, without)) return;
        ((item.f && item.f[key]) || []).forEach(function (value) {
          counts[value] = (counts[value] || 0) + 1;
        });
      });

      /* A picked option that now counts zero still has to render, or clearing
         it becomes impossible from the panel that set it. */
      (state[key] || []).forEach(function (value) {
        if (!(value in counts)) counts[value] = 0;
      });

      /* And a scale group keeps its whole run - see SCALE_GROUPS. */
      if (SCALE_GROUPS[key]) {
        Object.keys(Facets.scale || {}).forEach(function (id) {
          if (id.indexOf(key + ' ') !== 0) return;
          var value = Facets.scale[id];
          if (!(value in counts)) counts[value] = 0;
        });
      }

      var options = Object.keys(counts).map(function (value) {
        return { value: value, count: counts[value], selected: (state[key] || []).indexOf(value) !== -1 };
      }).filter(function (option) {
        return option.selected || !Facets.universal[key + ' ' + option.value];
      });

      return Facets.sort(key, options);
    },

    sort: function (key, options) {
      if (key === 'size') {
        return options.sort(function (a, b) {
          return parseFloat(a.value.split('|')[1]) - parseFloat(b.value.split('|')[1]);
        });
      }
      if (key === 'gender' || key === 'availability') {
        var order = key === 'gender' ? GENDER_ORDER : AVAILABILITY_ORDER;
        return options.sort(function (a, b) {
          var ai = order.indexOf(a.value);
          var bi = order.indexOf(b.value);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
      }
      /* Biggest bucket first: it is the one most likely to be wanted, and it
         puts the long tail where a shopper stops reading. */
      return options.sort(function (a, b) {
        if (b.count !== a.count) return b.count - a.count;
        return a.value < b.value ? -1 : 1;
      });
    },

    /** Every group with at least one option, ready to render. */
    groups: function (state) {
      state = state || {};
      return Facets.GROUPS.map(function (group) {
        return {
          key: group.key,
          label: group.label,
          options: Facets.options(group.key, state)
        };
      }).filter(function (group) {
        return group.options.length > 1 || (state[group.key] || []).length > 0;
      });
    }
  };

  // -------------------------------------------------------------------------
  // The deck
  //
  // Filtering and paging both happen here now, over the catalogue in memory.
  // Tiles are built in JS rather than fetched as Liquid: the deck already had
  // the data, and a page of 25 tiles was 1 request for the deck plus 25 for
  // the Favorites pane's version of the same markup.
  // -------------------------------------------------------------------------

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function titleCase(value) {
    return String(value)
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* The same [data-size] span snippets/product-size.liquid writes, so
     size-units.js paints it without knowing which of the two made it. */
  function sizeSpan(item, className) {
    if (!item.us && !item.ns && !item.nv) return '';
    return '<span class="' + className + '" data-size' +
      ' data-size-us="' + escapeHtml(item.us) + '"' +
      ' data-size-category="' + escapeHtml(item.ch) + '"' +
      ' data-size-native="' + escapeHtml(item.ns) + '"' +
      ' data-size-native-unit="' + escapeHtml(item.nu) + '"' +
      ' data-size-native-value="' + escapeHtml(item.nv) + '"' +
      '>' + escapeHtml(item.us ? 'US ' + item.us : (item.ns || item.nv)) + '</span>';
  }

  function tileHtml(item, eager) {
    var photo = item.im
      ? '<img src="' + escapeHtml(item.im) + '" alt="' + escapeHtml(item.al) + '"' +
        (eager ? ' loading="eager" fetchpriority="high"' : ' loading="lazy"') +
        ' decoding="async" width="300" height="375">'
      : '<span class="tile-photo-empty" aria-hidden="true"></span>';

    return '<article class="tile" data-tile data-handle="' + escapeHtml(item.h) + '"' +
      ' data-title="' + escapeHtml(item.t) + '">' +
      '<div class="tile-portrait">' +
      '<button type="button" class="tile-photo" data-tile-open aria-label="View ' +
        escapeHtml(item.t) + '">' + photo + '</button>' +
      (item.a ? '' : '<span class="tile-flag tile-flag--sold">' + escapeHtml(cartStrings().sold || 'Sold') + '</span>') +
      '<span class="tile-flag tile-flag--fav" data-tile-flag hidden>' +
        escapeHtml(cartStrings().favoriteFlag || 'Favorite') + '</span>' +
      '<div class="tile-caption">' +
      /* On a phone the title is the caption and the rest is trim: the grid is
         two columns wide there, so "Men's Black Croc Chelsea Boots" is what
         tells the pairs apart, not a size and a stock code. Both of those stay
         in the markup and shrink - see browse.css. */
      '<span class="tile-title">' + escapeHtml(item.t) + '</span>' +
      '<span class="tile-meta">' +
      sizeSpan(item, 'product-size tile-size') +
      (item.sk ? '<span class="tile-sku">' + escapeHtml(item.sk) + '</span>' : '') +
      '</span>' +
      '</div></div></article>';
  }

  function renderTiles(pane, items) {
    if (!items.length) return false;
    var html = '<div class="portrait-grid" data-deck-grid>';
    for (var i = 0; i < items.length; i += 1) html += tileHtml(items[i], i < 5);
    html += '</div>';
    pane.slot.innerHTML = html;
    applySizes(pane.slot);
    syncTiles(pane);
    return true;
  }

  function applyDeckHeading(pane) {
    var heading = pane.root.querySelector('.deck-toolbar h2');
    if (heading) heading.textContent = 'Browse';
  }

  /** Re-run the filter and repaint the current page of it. */
  function renderDeck(pane, options) {
    if (!Catalog.ready) return;

    if (Catalog.failed) {
      pane.setEmpty('Could not load products.');
      pane.setCount('');
      renderPager(pane);
      return;
    }

    deck.results = Facets.filter(deck.filters);
    deck.totalPages = Math.max(1, Math.ceil(deck.results.length / deck.perPage));
    if (deck.page > deck.totalPages) deck.page = deck.totalPages;
    if (deck.page < 1) deck.page = 1;

    applyDeckHeading(pane);

    var start = (deck.page - 1) * deck.perPage;
    var page = deck.results.slice(start, start + deck.perPage);

    if (!renderTiles(pane, page)) {
      pane.setEmpty(hasFilters()
        ? 'No products match these filters.'
        : 'No products to show.');
      pane.setCount('0 products');
      renderPager(pane);
      return;
    }

    pane.setCount(
      deck.results.length + ' product(s)' +
      (deck.totalPages > 1 ? ' · page ' + deck.page + ' of ' + deck.totalPages : '')
    );
    renderPager(pane);

    /* Keep the open card if it survived the filter - re-opening the first tile
       on every repaint would throw away what the shopper was reading. */
    var keep = pane.currentHandle && pane.tile(pane.currentHandle);
    if (keep) {
      setActiveTile(pane, pane.currentHandle);
    } else if (!options || options.open !== false) {
      autoOpenFirst(pane);
    }
  }

  function hasFilters() {
    for (var key in deck.filters) {
      if (Object.prototype.hasOwnProperty.call(deck.filters, key) &&
          deck.filters[key] && deck.filters[key].length) return true;
    }
    return false;
  }

  // -------------------------------------------------------------------------
  // The URL is the state
  //
  // A filtered deck has to be a place you can send someone. Everything the
  // shopper picked goes in the query string and is read back on load, so a link
  // to "women's black heels, page 2, this pair" reopens exactly that.
  //
  // Written with `replaceState`, not `pushState`: filters tick several times in
  // a row while someone narrows things down, and pushing each one turns the
  // back button into an undo trail twelve steps deep that they then have to
  // climb out of to leave the site. The address bar stays shareable either way;
  // this way Back still means back.
  //
  // Deliberately NOT in the URL: the size unit and the open tab. Both are
  // preferences of the person looking, not properties of what is being looked
  // at - a link shared by someone browsing in EU should still open in whatever
  // unit the recipient reads sizes in, and nobody wants to be dropped into a
  // stranger's Favorites.
  // -------------------------------------------------------------------------

  /* Short names, because the shopper sees this. `filter.p.tag=...` is Shopify's
     spelling and it is unreadable by the fourth facet. */
  var URL_KEYS = {
    size: 'size',
    gender: 'dept',
    style: 'style',
    color: 'color',
    availability: 'stock',
    tags: 'tag'
  };

  var MAX_URL_VALUES = 24;      // a hand-edited URL cannot make us build a novel
  var MAX_URL_VALUE_LEN = 60;

  /* A size is keyed `men|9` internally. The bar is legal in a query string but
     percent-encodes into noise, so it travels as `men-9` - and only the FIRST
     dash comes back, which keeps `men-10.5` intact. */
  function encodeFacetValue(group, value) {
    var raw = group === 'size' ? String(value).replace('|', '-') : String(value);
    return encodeURIComponent(raw);
  }

  /* decodeURIComponent throws a URIError on a stray percent - "%%%" is enough -
     and a hand-typed or truncated link is exactly where that shows up. One bad
     value must not take the whole deck down with it, so it is dropped and the
     rest of the address still applies. */
  function safeDecode(raw) {
    try {
      return decodeURIComponent(raw);
    } catch (error) {
      return null;
    }
  }

  function decodeFacetValue(group, raw) {
    var value = safeDecode(raw);
    if (value === null) return null;
    return group === 'size' ? value.replace('-', '|') : value;
  }

  /* Parsed by hand rather than with URLSearchParams, which decodes as it reads:
     a value with a comma in it would come back already unescaped and then split
     down the middle. Values stay raw until they have been separated. */
  function rawQuery() {
    var out = {};
    var query = String(window.location.search || '').replace(/^\?/, '');
    if (!query) return out;
    query.split('&').forEach(function (pair) {
      if (!pair) return;
      var eq = pair.indexOf('=');
      var key = eq === -1 ? pair : pair.slice(0, eq);
      var name = safeDecode(key);
      if (name !== null) out[name] = eq === -1 ? '' : pair.slice(eq + 1);
    });
    return out;
  }

  function readUrlState() {
    var params = rawQuery();
    var filters = {};

    Facets.GROUPS.forEach(function (group) {
      var raw = params[URL_KEYS[group.key]];
      if (!raw) return;
      var values = raw.split(',')
        .slice(0, MAX_URL_VALUES)
        .map(function (part) { return decodeFacetValue(group.key, part); })
        .filter(function (value) {
          return value !== null && value !== '' && value.length <= MAX_URL_VALUE_LEN;
        });
      if (values.length) filters[group.key] = values;
    });

    var page = parseInt(params.page, 10);
    var handle = params.pair ? safeDecode(params.pair) : null;

    return {
      filters: filters,
      page: page > 1 ? page : 1,
      handle: handle && handle.length <= 200 ? handle : null
    };
  }

  function urlQuery() {
    var parts = [];

    Facets.GROUPS.forEach(function (group) {
      var values = deck.filters[group.key];
      if (!values || !values.length) return;
      parts.push(URL_KEYS[group.key] + '=' + values.map(function (value) {
        return encodeFacetValue(group.key, value);
      }).join(','));
    });

    if (deck.page > 1) parts.push('page=' + deck.page);

    var pane = panes.browse;
    if (pane && pane.pickedHandle) {
      parts.push('pair=' + encodeURIComponent(pane.pickedHandle));
    }

    return parts.length ? '?' + parts.join('&') : '';
  }

  /* Off until the first render has settled, so the URL is read before it is
     ever written - otherwise opening a shared link would blank its own
     parameters on the way past. */
  var urlSyncOn = false;

  function syncUrl() {
    if (!urlSyncOn) return;
    try {
      window.history.replaceState({}, '', window.location.pathname + urlQuery());
    } catch (error) {
      /* file://, sandboxed iframes - the deck works, the address bar does not */
    }
  }

  /**
   * Point the deck at a selection. One entry point for every group, so a
   * change of style while a colour is set repaints once rather than twice.
   */
  function setFilters(next) {
    deck.filters = next || {};
    deck.page = 1;

    var pane = panes.browse;
    if (!pane) return;
    pane.loaded = true;
    showTab('browse');
    renderDeck(pane);
    syncUrl();
  }

  function goToPage(pane, page) {
    if (!pane || pane.key !== 'browse') return;
    if (page < 1 || page > deck.totalPages || page === deck.page) return;
    deck.page = page;
    renderDeck(pane, { open: false });

    /* Both layouts, one call. On a wide screen the grid is its own scroll box
       and this rewinds it; on a phone the grid is the page, and scrolling it
       into view lands under the pinned card (see scroll-margin-top). Paging is
       a deliberate act, so a scroll here is expected - unlike opening a tile,
       which must never move the page. */
    if (pane.slot) {
      pane.slot.scrollTop = 0;
      if (pane.slot.scrollIntoView) pane.slot.scrollIntoView({ block: 'start' });
    }
    autoOpenFirst(pane);
    syncUrl();
  }

  /**
   * A window of five numbers around the current page, with the first and last
   * always reachable and an ellipsis where the run breaks - so a forty-page
   * catalogue does not render forty buttons.
   */
  function pageWindow(current, total) {
    var pages = [];
    var start = Math.max(1, current - 2);
    var end = Math.min(total, start + 4);
    start = Math.max(1, Math.min(start, end - 4));

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('gap');
    }
    for (var i = start; i <= end; i += 1) pages.push(i);
    if (end < total) {
      if (end < total - 1) pages.push('gap');
      pages.push(total);
    }
    return pages;
  }

  function renderPager(pane) {
    var pager = pane.pager;
    if (!pager) return;

    if (deck.totalPages <= 1) {
      pager.innerHTML = '';
      pager.hidden = true;
      return;
    }

    var labels = cartStrings();
    var html = '';

    html += '<button type="button" class="pager-step" data-page="' + (deck.page - 1) + '"' +
      (deck.page <= 1 ? ' disabled' : '') + '>' + (labels.previous || 'Previous') + '</button>';

    html += '<span class="pager-pages">';
    pageWindow(deck.page, deck.totalPages).forEach(function (entry) {
      if (entry === 'gap') {
        html += '<span class="pager-gap">…</span>';
        return;
      }
      html += '<button type="button" class="pager-page' +
        (entry === deck.page ? ' is-current' : '') + '" data-page="' + entry + '"' +
        (entry === deck.page ? ' aria-current="page"' : '') + '>' + entry + '</button>';
    });
    html += '</span>';

    html += '<button type="button" class="pager-step" data-page="' + (deck.page + 1) + '"' +
      (deck.page >= deck.totalPages ? ' disabled' : '') + '>' + (labels.next || 'Next') + '</button>';

    pager.innerHTML = html;
    pager.hidden = false;
  }

  // -------------------------------------------------------------------------
  // Favorites pane
  // -------------------------------------------------------------------------

  function renderFavorites(pane) {
    if (!Catalog.ready) return;

    var items = Favorites.list().map(function (handle) {
      return Catalog.byHandle(handle);
    }).filter(Boolean);

    if (!items.length) {
      pane.setEmpty('No favorites yet — add some from Browse.');
      pane.setCount('0 favorites');
      return;
    }

    renderTiles(pane, items);
    pane.setCount(items.length + ' favorite(s)');

    if (!pane.currentHandle || !pane.tile(pane.currentHandle)) autoOpenFirst(pane);
  }

  // -------------------------------------------------------------------------
  // Tiles + detail card
  // -------------------------------------------------------------------------

  function syncTiles(pane) {
    pane.tiles().forEach(function (tile) {
      var handle = tile.getAttribute('data-handle');
      var favorited = Favorites.has(handle);
      tile.setAttribute('data-favorite', favorited ? 'true' : 'false');
      var flag = tile.querySelector('[data-tile-flag]');
      if (flag) flag.hidden = !favorited;
    });
    syncFavCount();
  }

  /* Scoped to the document, not the app: the header renders its own badge
     outside [data-browse-app]. */
  function syncFavCount() {
    var count = Favorites.count();
    Array.prototype.forEach.call(document.querySelectorAll('[data-fav-count]'), function (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  }

  function autoOpenFirst(pane) {
    var first = pane.tiles()[0];
    if (first) openProduct(pane, first.getAttribute('data-handle'), { auto: true });
  }

  /**
   * `reveal` is off by default, and that is the whole point.
   *
   * The deck used to be a strip inside its own scroll box, so bringing the
   * selected tile into view scrolled that box. It is the page now, so
   * scrollIntoView scrolls the window - and calling it after every open meant
   * tapping a tile halfway down the grid, or changing a filter, yanked the
   * page back up to wherever the first tile was. Only keyboard stepping asks
   * for it, because only keyboard stepping can move the selection to
   * something that is genuinely off screen.
   */
  function setActiveTile(pane, handle, reveal) {
    pane.tiles().forEach(function (tile) {
      tile.classList.toggle('is-active', tile.getAttribute('data-handle') === handle);
    });
    if (!reveal) return;
    var active = pane.tile(handle);
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  function openProduct(pane, handle, options) {
    if (!handle) return Promise.resolve();
    pane.currentHandle = handle;
    setActiveTile(pane, handle, !!(options && options.reveal));

    /* Which pair is open is part of the address too - a link to a filtered deck
       that lands on a different shoe than the one being talked about is half a
       link. But only a pair somebody actually chose: the deck opens the first
       tile by itself on every render, and stamping that into the address means
       the bare homepage rewrites itself to a product nobody picked, and paging
       leaves the previous page's shoe sitting in the URL. An automatic open
       clears it instead. Favorites is somebody's own list and never writes. */
    if (pane.key === 'browse') {
      pane.pickedHandle = (options && options.auto) ? null : handle;
      syncUrl();
    }

    var token = (pane.__token = (pane.__token || 0) + 1);
    pane.detail.innerHTML = '<div class="pane-loading">Loading&hellip;</div>';

    return fetchSection(
      '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTIONS.detail,
      SECTIONS.detail
    ).then(function (html) {
      if (token !== pane.__token) return;   // a newer open won
      pane.detail.innerHTML = html;
      /* The detail pane is its own scroll area on phones, so a product opened
         from halfway down the previous one has to start at its own top. */
      pane.detail.scrollTop = 0;
      applySizes(pane.detail);
      initCarousel(pane.detail);
      syncFavButton(pane);
    }).catch(function (error) {
      console.error('[browse] detail load failed', error);
      pane.detail.innerHTML = '<div class="pane-empty">Could not load this product.</div>';
    });
  }

  function syncFavButton(pane) {
    var button = pane.detail.querySelector('[data-fav-toggle]');
    if (!button) return;
    var favorited = Favorites.has(button.getAttribute('data-handle'));
    button.setAttribute('aria-pressed', favorited ? 'true' : 'false');
    var label = button.querySelector('[data-fav-label]');
    if (label) {
      label.textContent = favorited
        ? button.getAttribute('data-label-remove')
        : button.getAttribute('data-label-add');
    }
  }

  // -------------------------------------------------------------------------
  // Info sheet
  //
  // The description used to expand in place on the card, and its open state
  // was remembered so that stepping through products did not keep collapsing
  // it. As a sheet neither applies: it is opened deliberately, for one product,
  // and closed when the reading is done.
  // -------------------------------------------------------------------------

  var infoFocus = null;

  function infoSheetRoot() {
    return document.querySelector('[data-info-sheet]');
  }

  function infoSheetOpen() {
    var root = infoSheetRoot();
    return !!(root && !root.hidden);
  }

  function openInfoSheet(pane) {
    var root = infoSheetRoot();
    var source = pane.detail.querySelector('[data-more-source]');
    if (!root || !source) return false;

    var body = root.querySelector('[data-is-body]');
    var title = root.querySelector('[data-is-title]');
    var name = pane.detail.querySelector('.pcard-title');

    /* Copied, not moved: the card is replaced wholesale on the next product
       and would take the description with it. */
    if (body) body.innerHTML = source.innerHTML;
    if (title && name) title.textContent = name.textContent;

    infoFocus = document.activeElement;
    root.hidden = false;
    document.body.classList.add('has-sheet');
    if (body) body.scrollTop = 0;

    var close = root.querySelector('[data-is-close]');
    if (close && close.focus) close.focus();
    return true;
  }

  function closeInfoSheet() {
    var root = infoSheetRoot();
    if (!root || root.hidden) return;
    root.hidden = true;
    document.body.classList.remove('has-sheet');
    if (infoFocus && infoFocus.focus) infoFocus.focus();
    infoFocus = null;
  }

  /**
   * Photo carousel.
   *
   * Two things move when a thumbnail is picked, and they move at different
   * times on purpose. The *selection* - the ring on the thumb and the counter -
   * moves on the click, synchronously. The *photograph* swaps when its bytes
   * arrive, behind a token guard so a slow image can never land on top of a
   * newer choice.
   *
   * It used to do both after the preload, and that is what made the thumbs feel
   * unreliable: `data-large` is a 1200px file, uncached the first time each one
   * is picked, so on a real store the first click on a thumb did nothing at all
   * for as long as the download took. A click that produces no acknowledgement
   * reads as a click that was not received - so it gets clicked again, and the
   * second one looks like the one that worked.
   */
  function initCarousel(scope) {
    var card = scope.querySelector('[data-pcard]');
    if (!card) return;

    var main = card.querySelector('[data-pcard-main-img]');
    var counter = card.querySelector('[data-pcard-counter]');
    var thumbs = Array.prototype.slice.call(card.querySelectorAll('[data-pcard-thumb]'));
    var sources = thumbs.map(function (thumb) { return thumb.getAttribute('data-large'); })
      .filter(Boolean);

    if (!sources.length && main) sources = [main.getAttribute('src')];
    if (!sources.length) return;

    var index = 0;
    var token = 0;

    function paint() {
      if (counter) counter.textContent = (index + 1) + ' / ' + sources.length;
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
      });
    }

    function show(next) {
      var target = ((next % sources.length) + sources.length) % sources.length;
      var src = sources[target];

      /* The selection is the answer to "did that click land", so it is set
         before anything is fetched. `index` moves with it, which also means the
         next click computes its target from what is on screen rather than from
         whatever the last finished download left behind. */
      index = target;
      paint();

      var mine = ++token;
      return preload(src).then(function () {
        if (mine !== token) return;
        if (main) main.setAttribute('src', src);
      });
    }

    /* Absolute, not relative. The click handler used to read the active thumb
       out of the DOM, subtract, and hand back a delta - two conversions and a
       DOM read to express something the button already knows. */
    card.__go = function (next) { show(next); };
    card.__step = function (delta) { show(index + delta); };
    paint();

    // Warm the next photo so the first click is instant.
    if (sources.length > 1) preload(sources[1]);
  }

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  function toggleFavorite(pane, handle) {
    if (!handle) return;
    var favorited = Favorites.toggle(handle);

    var tile = pane.tile(handle);
    if (tile) {
      tile.setAttribute('data-favorite', favorited ? 'true' : 'false');
      var flag = tile.querySelector('[data-tile-flag]');
      if (flag) flag.hidden = !favorited;
    }

    syncFavButton(pane);
    syncFavCount();

    if (pane.key === 'browse') {
      panes.favorites.dirty = true;
    } else if (!favorited && tile) {
      // Un-favoriting from the saved list: step on, then repaint without it.
      var next = tile.nextElementSibling || tile.previousElementSibling;
      if (next) pane.currentHandle = next.getAttribute('data-handle');
      renderFavorites(pane);
    }
  }

  function step(pane, delta) {
    var tiles = pane.tiles();
    if (!tiles.length) return false;
    var current = tiles.findIndex(function (tile) {
      return tile.getAttribute('data-handle') === pane.currentHandle;
    });
    var next = current === -1 ? 0 : current + delta;
    if (next < 0 || next >= tiles.length) return false;
    openProduct(pane, tiles[next].getAttribute('data-handle'), { reveal: true });
    return true;
  }

  // -------------------------------------------------------------------------
  // Cart
  // -------------------------------------------------------------------------

  /* The deck is now a storefront, not just a browsing tool, so the detail card
     posts to Shopify's own cart API. There is no cart drawer on this layout -
     the count in the top bar links through to /cart, which runs on Dawn's
     theme.liquid like every other non-Browse page. */

  var CART = {
    add: (window.routes && window.routes.cart_add_url) || '/cart/add',
    url: (window.routes && window.routes.cart_url) || '/cart'
  };

  function cartStrings() {
    return window.browseStrings || {};
  }

  function syncCartCount(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]'), function (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) { if (cart) syncCartCount(cart.item_count || 0); })
      .catch(function () { /* a cart count is not worth an error state */ });
  }

  /**
   * Add the detail card's selected variant to the cart.
   *
   * Deliberately fetch-based rather than a plain form POST: a form submit would
   * navigate away from the deck and lose the shopper's place, which is the one
   * thing this interface exists to keep.
   */
  function addToCart(form) {
    if (form.__busy) return;
    var button = form.querySelector('[data-cart-submit]');
    var label = button ? button.querySelector('[data-cart-label]') : null;
    var strings = cartStrings();
    var previous = label ? label.textContent : '';

    form.__busy = true;
    if (button) button.disabled = true;
    if (label && strings.adding) label.textContent = strings.adding;

    fetch(CART.add + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: Number(form.querySelector('[name="id"]').value),
          quantity: 1
        }]
      })
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.description || body.message || 'cart');
          return body;
        });
      })
      .then(function () {
        if (label) label.textContent = strings.added || 'Added';
        form.setAttribute('data-cart-state', 'added');
        return refreshCartCount();
      })
      .catch(function (error) {
        console.error('[browse] add to cart failed', error);
        form.setAttribute('data-cart-state', 'error');
        var note = form.querySelector('[data-cart-error]');
        if (note) {
          note.textContent = error.message || strings.cartError || '';
          note.hidden = false;
        }
        if (label) label.textContent = previous;
      })
      .then(function () {
        form.__busy = false;
        if (button) button.disabled = false;
      });
  }

  // -------------------------------------------------------------------------
  // Photo viewer
  //
  // A shoe is bought on its photographs, and the card can only ever give them a
  // third of a phone screen. Tapping the photo hands over the whole one.
  //
  // It reads the photo set off the open detail card's thumb buttons rather than
  // from the catalogue: the card already fetched the full-size URLs, and taking
  // them from the DOM means the viewer works for whatever is on screen without
  // a second source of truth about which product that is.
  // -------------------------------------------------------------------------

  var viewer = { photos: [], index: 0, lastFocus: null, animating: false };

  function viewerRoot() {
    return document.querySelector('[data-photo-viewer]');
  }

  function viewerOpen() {
    var root = viewerRoot();
    return !!(root && !root.hidden);
  }

  function openViewer(pane, startIndex) {
    var root = viewerRoot();
    var card = pane.detail.querySelector('[data-pcard]');
    if (!root || !card) return false;

    var thumbs = Array.prototype.slice.call(card.querySelectorAll('[data-pcard-thumb]'));
    viewer.photos = thumbs.map(function (thumb) {
      var small = thumb.querySelector('img');
      return {
        large: thumb.getAttribute('data-large'),
        small: small ? small.getAttribute('src') : thumb.getAttribute('data-large')
      };
    }).filter(function (photo) { return !!photo.large; });

    /* A single-photo product still opens - seeing one shoe big is the point,
       and the arrows and strip hide themselves below. */
    if (!viewer.photos.length) {
      var main = card.querySelector('[data-pcard-main-img]');
      if (!main) return false;
      viewer.photos = [{ large: main.getAttribute('src'), small: main.getAttribute('src') }];
    }

    viewer.index = Math.min(Math.max(startIndex || 0, 0), viewer.photos.length - 1);
    viewer.lastFocus = document.activeElement;

    var strip = root.querySelector('[data-pv-strip]');
    if (strip) {
      strip.innerHTML = viewer.photos.map(function (photo, i) {
        return '<button type="button" class="pviewer-thumb" data-pv-go="' + i + '"' +
          ' aria-label="Photo ' + (i + 1) + '">' +
          '<img src="' + photo.small + '" alt="" loading="lazy" decoding="async"' +
          ' width="300" height="375">' +
          '</button>';
      }).join('');
    }

    root.hidden = false;
    root.classList.toggle('is-single', viewer.photos.length < 2);
    document.body.classList.add('has-viewer');
    viewer.animating = false;
    paintViewer();

    var close = root.querySelector('[data-pv-close]');
    if (close && close.focus) close.focus();
    return true;
  }

  function closeViewer() {
    var root = viewerRoot();
    if (!root || root.hidden) return;
    root.hidden = true;
    document.body.classList.remove('has-viewer');
    if (viewer.lastFocus && viewer.lastFocus.focus) viewer.lastFocus.focus();
    viewer.lastFocus = null;
  }

  function paintViewer(instant) {
    var root = viewerRoot();
    if (!root) return;
    var total = viewer.photos.length;
    if (!total) return;

    /* Three slides: what came before, what is showing, what comes next. The
       track is recentred with the transition off, so the swap the eye never
       sees is the one that makes an endless carousel out of three elements. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-pv-slide]'), function (slide) {
      var offset = parseInt(slide.getAttribute('data-pv-slide'), 10);
      var photo = viewer.photos[((viewer.index + offset) % total + total) % total];
      slide.setAttribute('src', photo ? photo.large : '');
      slide.hidden = total < 2 && offset !== 0;
    });

    trackTo(0, instant === true ? 'instant' : 'instant');

    var count = root.querySelector('[data-pv-count]');
    if (count) count.textContent = (viewer.index + 1) + ' / ' + total;

    Array.prototype.forEach.call(root.querySelectorAll('[data-pv-go]'), function (thumb) {
      var isActive = parseInt(thumb.getAttribute('data-pv-go'), 10) === viewer.index;
      thumb.classList.toggle('is-active', isActive);
      if (isActive && thumb.scrollIntoView) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
    });

    preload((viewer.photos[(viewer.index + 2) % total] || {}).large);
  }

  /**
   * Move the track. `delta` is in slides: 0 is centred, -1 and 1 are the
   * neighbours. `offset` nudges it by pixels, which is what a finger does.
   */
  function trackTo(delta, mode, offset) {
    var track = viewerRoot() && viewerRoot().querySelector('[data-pv-track]');
    if (!track) return;
    track.style.transition = mode === 'animate'
      ? 'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)'
      : 'none';
    track.style.transform = 'translate3d(calc(' + ((-1 - delta) * 33.3333) + '% + ' +
      (offset || 0) + 'px), 0, 0)';
  }

  /** Step with the slide animation, then recentre once it has landed. */
  function stepViewer(delta) {
    var total = viewer.photos.length;
    if (total < 2 || viewer.animating || !delta) return;

    viewer.animating = true;
    trackTo(delta, 'animate');

    window.setTimeout(function () {
      viewer.index = ((viewer.index + delta) % total + total) % total;
      viewer.animating = false;
      paintViewer();
    }, 260);
  }

  /**
   * Drag. The track follows the finger one-to-one while it is down, and on
   * release either carries on to the next photo or springs back to this one.
   *
   * Resistance only applies with a single photo, where there is nothing to
   * drag to: the pull is damped to a third so the picture gives a little and
   * comes back, rather than either moving freely to nowhere or feeling nailed
   * down. With more than one the carousel wraps, so there is no edge to guard.
   */
  function bindViewerDrag(root) {
    var stage = root.querySelector('[data-pv-stage]');
    if (!stage) return;

    var startX = 0;
    var startY = 0;
    var dx = 0;
    var dragging = false;
    var decided = false;
    var horizontal = false;

    function width() {
      return stage.getBoundingClientRect().width || 1;
    }

    stage.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1 || viewer.animating) return;
      if (event.target.closest('[data-pv-strip], button')) return;
      dragging = true;
      decided = false;
      horizontal = false;
      dx = 0;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });

    stage.addEventListener('touchmove', function (event) {
      if (!dragging) return;
      var touch = event.touches[0];
      var moveX = touch.clientX - startX;
      var moveY = touch.clientY - startY;

      /* Decide once, after 10px, whether this gesture belongs to the carousel
         or to the page. Deciding on every frame makes a diagonal drag flicker
         between the two. */
      if (!decided && (Math.abs(moveX) > 10 || Math.abs(moveY) > 10)) {
        decided = true;
        horizontal = Math.abs(moveX) > Math.abs(moveY);
        if (!horizontal) dragging = false;
      }
      if (!decided || !horizontal) return;

      event.preventDefault();
      dx = viewer.photos.length > 1 ? moveX : moveX / 3;
      trackTo(0, 'instant', dx);
    }, { passive: false });

    function release() {
      if (!dragging) return;
      dragging = false;
      if (!horizontal) return;

      /* A quarter of the screen is the commit point - far enough that a stray
         thumb does not turn the page, close enough that a deliberate flick
         never feels ignored. */
      var threshold = width() * 0.25;
      if (viewer.photos.length > 1 && Math.abs(dx) > threshold) {
        stepViewer(dx < 0 ? 1 : -1);
      } else {
        trackTo(0, 'animate');
      }
      dx = 0;
    }

    stage.addEventListener('touchend', release, { passive: true });
    stage.addEventListener('touchcancel', release, { passive: true });
  }

  // -------------------------------------------------------------------------
  // Request a size
  // -------------------------------------------------------------------------

  /* Shopify's own contact form, posted with fetch so the deck never navigates.

     A successful post redirects to ?contact_posted=true and fetch follows that
     redirect, so the final URL is the only reliable success signal: a rejected
     submission comes back 200 with the form re-rendered and its errors in the
     HTML, which is indistinguishable from success by status alone. */

  var lastRequestFocus = null;

  function dialogEl(selector) {
    var root = document.querySelector('[data-size-request-modal]');
    return root ? root.querySelector(selector) : null;
  }

  function openSizeRequest(trigger) {
    var root = document.querySelector('[data-size-request-modal]');
    if (!root) return false;

    lastRequestFocus = document.activeElement;

    var title = trigger ? trigger.getAttribute('data-product-title') : '';
    var sku = trigger ? trigger.getAttribute('data-product-sku') : '';

    var line = dialogEl('[data-sr-product]');
    var nameEl = dialogEl('[data-sr-product-name]');
    var skuEl = dialogEl('[data-sr-product-sku]');
    if (nameEl) nameEl.textContent = title || '';
    if (skuEl) {
      skuEl.textContent = sku || '';
      skuEl.hidden = !sku;
    }
    if (line) line.hidden = !title && !sku;

    var productField = dialogEl('[data-sr-field-product]');
    var skuField = dialogEl('[data-sr-field-sku]');
    if (productField) productField.value = title || '';
    if (skuField) skuField.value = sku || '';

    /* Reopened after a send, this has to be a blank form again rather than the
       thank-you from the last pair. */
    var form = dialogEl('.sr-form');
    var success = dialogEl('[data-sr-success]');
    var error = dialogEl('[data-sr-error]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
    if (error) error.hidden = true;

    root.hidden = false;
    document.body.style.overflow = 'hidden';

    var first = dialogEl('input[name="contact[name]"]');
    if (first && first.focus) first.focus();
    return true;
  }

  function closeSizeRequest() {
    var root = document.querySelector('[data-size-request-modal]');
    if (!root || root.hidden) return;
    root.hidden = true;
    document.body.style.overflow = '';
    if (lastRequestFocus && lastRequestFocus.focus) lastRequestFocus.focus();
    lastRequestFocus = null;
  }

  /* ---------------------------------------------------------------------
     Sending a size request

     This does NOT post with fetch, and that is not an oversight. Shopify
     protects `{% form 'contact' %}` with an invisible hCaptcha: its own script
     hooks the form's submit event, mints a token and writes it into an
     `h-captcha-response` field just before the browser posts. A fetch skips
     that handler entirely, so the token is never minted and the store answers

         400  Missing CAPTCHA token

     - which is what a shopper saw as "could not send your request".
     `window.Shopify.captcha.protect(form, callback)` does not rescue it
     either: it adds the field and fires the callback when the *widget* is
     ready, and the value is still empty. Only a real submit fills it. Both
     were tried against the live store; only the native submit came back
     `?contact_posted=true`.

     So the browser posts the form the ordinary way and the page navigates.
     What is left to do is make the round trip invisible: stash where the deck
     was standing, and put it back when Shopify returns.
     --------------------------------------------------------------------- */

  function rememberDeck() {
    var pane = panes[activeTab];
    writeStore(window.sessionStorage, KEYS.resume, {
      sent: true,
      tab: activeTab,
      page: deck.page,
      filters: deck.filters,
      handle: pane ? pane.currentHandle : null
    });
  }

  /* Only our own dialog reopens on the way back. `contact_posted=true` lands on
     the URL after *any* contact form on the store, Dawn's own page included,
     and popping a size-request thank-you at someone who used that one would be
     a non-sequitur. The flag in sessionStorage is what makes it ours. */
  function resumeAfterSizeRequest() {
    var state = readStore(window.sessionStorage, KEYS.resume, null);
    try { window.sessionStorage.removeItem(KEYS.resume); } catch (error) {}
    if (!state || !state.sent) return null;

    /* The flag is written immediately before a submit and cleared on the next
       read, so finding it here means one just happened. Whether it *worked* is
       the query string's business: Shopify sends the shopper back either way,
       and only adds the parameter when the message actually went. Without this
       branch a rejected request lands back on the deck saying nothing at all. */
    state.ok = window.location.search.indexOf('contact_posted=true') !== -1;

    /* The parameter has done its job - left on it would re-fire this on every
       reload and every step back through history - but the rest of the query
       string is the shopper's filters and must survive. syncUrl() rewrites the
       address from the restored deck a moment later, which drops
       contact_posted and keeps everything else; nothing is stripped here. */
    return state;
  }

  /* Reopen the dialog on the answer it came back with: the thank-you, or the
     form again with a message on it. */
  function showSizeRequestResult(ok) {
    var root = document.querySelector('[data-size-request-modal]');
    if (!root) return;

    var form = dialogEl('.sr-form');
    var success = dialogEl('[data-sr-success]');
    var error = dialogEl('[data-sr-error]');

    if (form) form.hidden = ok;
    if (success) success.hidden = !ok;
    if (error) {
      error.hidden = ok;
      if (!ok) {
        error.textContent = cartStrings().srError || 'Could not send your request.';
      }
    }

    root.hidden = false;
    document.body.style.overflow = 'hidden';

    var focus = root.querySelector(ok ? '[data-sr-close]' : 'input[name="contact[name]"]');
    if (focus && focus.focus) focus.focus();
  }

  /* Runs on submit WITHOUT preventing it: stash the deck's place, then get out
     of the browser's way.

     It used to also fold Product / SKU / Size requested into the message body.
     That was doing two wrong things at once. Shopify passes every
     `contact[...]` field into the notification email as its own labelled line,
     so the summary was a second copy of three values already in the email -
     and building it meant overwriting the shopper's own textarea, so they
     watched what they had typed get replaced by a block of machine text at the
     moment they pressed Send. The hidden fields were always the better carrier:
     they are labelled, they cannot be edited by accident, and they still post
     when JavaScript never runs. */
  function prepareSizeRequest(form) {
    var strings = cartStrings();
    var button = form.querySelector('[data-sr-submit]');
    var label = form.querySelector('[data-sr-submit-label]');
    if (button) button.disabled = true;
    if (label && strings.srSending) label.textContent = strings.srSending;

    rememberDeck();
  }

  // -------------------------------------------------------------------------
  // Wiring
  // -------------------------------------------------------------------------

  function paneOf(node) {
    var shell = node.closest ? node.closest('[data-split-shell]') : null;
    return shell ? panes[shell.getAttribute('data-pane-key')] : null;
  }

  function bindEvents() {
    /* Document-scoped rather than app-scoped: the tabs live in the top bar
       section, outside [data-browse-app]. paneOf() returns null for anything
       outside a split shell, so the rest still only reacts inside the app. */
    document.addEventListener('click', function (event) {
      var target = event.target;

      var tab = target.closest('.tab');
      if (tab) {
        showTab(tab.getAttribute('data-tab'));
        return;
      }

      if (target.closest('[data-is-close]')) {
        event.preventDefault();
        closeInfoSheet();
        return;
      }

      if (target.closest('[data-pv-close]')) {
        event.preventDefault();
        closeViewer();
        return;
      }

      var pvStep = target.closest('[data-pv-step]');
      if (pvStep) {
        event.preventDefault();
        stepViewer(parseInt(pvStep.getAttribute('data-pv-step'), 10) || 1);
        return;
      }

      var pvGo = target.closest('[data-pv-go]');
      if (pvGo) {
        event.preventDefault();
        if (!viewer.animating) {
          viewer.index = parseInt(pvGo.getAttribute('data-pv-go'), 10) || 0;
          paintViewer();
        }
        return;
      }

      /* The backdrop is a way out too: everything inside the viewer that is
         not a control is dead space, and tapping dead space to leave is what a
         full-screen photo trains you to expect. */
      if (viewerOpen() && target.closest('[data-photo-viewer]') &&
          !target.closest('button, a, [data-pv-strip]')) {
        event.preventDefault();
        closeViewer();
        return;
      }

      var sizeTrigger = target.closest('[data-size-request]');
      if (sizeTrigger) {
        if (openSizeRequest(sizeTrigger)) event.preventDefault();
        return;
      }

      if (target.closest('[data-sr-close]')) {
        event.preventDefault();
        closeSizeRequest();
        return;
      }

      var pane = paneOf(target);
      if (!pane) return;

      var moreToggle = target.closest('[data-more-toggle]');
      if (moreToggle) {
        event.preventDefault();
        openInfoSheet(pane);
        return;
      }

      var favButton = target.closest('[data-fav-toggle]');
      if (favButton) {
        event.preventDefault();
        toggleFavorite(pane, favButton.getAttribute('data-handle'));
        return;
      }

      var pageButton = target.closest('[data-page]');
      if (pageButton) {
        event.preventDefault();
        if (!pageButton.disabled) {
          goToPage(pane, parseInt(pageButton.getAttribute('data-page'), 10) || 1);
        }
        return;
      }

      var thumb = target.closest('[data-pcard-thumb]');
      if (thumb) {
        event.preventDefault();
        var card = pane.detail.querySelector('[data-pcard]');
        if (card && card.__go) {
          card.__go(parseInt(thumb.getAttribute('data-index'), 10) || 0);
        }
        return;
      }

      if (target.closest('[data-pcard-main]')) {
        event.preventDefault();
        /* The photo used to advance to the next one on click, which is a
           smaller thing to do with a tap on the only picture of the product. */
        var card = pane.detail.querySelector('[data-pcard]');
        var thumbs = card ? card.querySelectorAll('[data-pcard-thumb].is-active') : null;
        var at = thumbs && thumbs.length
          ? parseInt(thumbs[0].getAttribute('data-index'), 10) || 0
          : 0;
        openViewer(pane, at);
        return;
      }

      var tile = target.closest('[data-tile]');
      if (tile) {
        event.preventDefault();
        openProduct(pane, tile.getAttribute('data-handle'));
      }
    });

    /* Submit rather than click, so Enter in the variant select works too. */
    document.addEventListener('submit', function (event) {
      /* Note the missing preventDefault. The size request is the one form here
         that must post the ordinary way - Shopify's hCaptcha only mints its
         token inside the browser's own submit, and a fetch gets a 400 back.
         See prepareSizeRequest. */
      var request = event.target.closest('.sr-form');
      if (request) {
        prepareSizeRequest(request);
        return;
      }

      var form = event.target.closest('[data-cart-form]');
      if (!form) return;
      event.preventDefault();
      addToCart(form);
    });

    /* Picking another variant clears the "Added" state and repaints
       availability from the option the shopper is actually looking at. */
    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-cart-variant]');
      if (!select) return;
      var form = select.closest('[data-cart-form]');
      if (!form) return;
      var option = select.options[select.selectedIndex];
      form.removeAttribute('data-cart-state');
      var idField = form.querySelector('[name="id"]');
      if (idField) idField.value = select.value;
      var button = form.querySelector('[data-cart-submit]');
      var label = form.querySelector('[data-cart-label]');
      var strings = cartStrings();
      var available = option && option.getAttribute('data-available') === 'true';
      if (button) button.disabled = !available;
      if (label) label.textContent = available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
      var note = form.querySelector('[data-cart-error]');
      if (note) note.hidden = true;
    });

    var pv = viewerRoot();
    if (pv) bindViewerDrag(pv);

    document.addEventListener('keydown', onKeydown);
  }

  function isTyping(element) {
    if (!element) return false;
    var tag = element.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable) return true;
    /* Space on a focused Add to cart button must add to the cart, not favorite
       the pair - the browser already fires the click, and the shortcut would
       fire on top of it. */
    return !!(element.closest && element.closest('[data-cart-form]'));
  }

  function onKeydown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    if (event.key === 'Escape') {
      if (viewerOpen()) { closeViewer(); return; }
      if (infoSheetOpen()) { closeInfoSheet(); return; }
      closeSizeRequest();
      return;
    }

    if (viewerOpen()) {
      if (event.key === 'ArrowRight') { stepViewer(1); event.preventDefault(); }
      if (event.key === 'ArrowLeft') { stepViewer(-1); event.preventDefault(); }
      return;
    }

    /* Everything below drives the deck behind whatever is over it. While any
       of the three is open, nothing but Escape should reach the deck. */
    var dialog = document.querySelector('[data-size-request-modal]');
    if ((dialog && !dialog.hidden) || infoSheetOpen()) return;

    if (isTyping(document.activeElement)) return;

    var pane = panes[activeTab];
    if (!pane) return;

    var handled = false;

    switch (event.key) {
      case 'ArrowRight':
        handled = step(pane, 1);
        break;
      case 'ArrowLeft':
        handled = step(pane, -1);
        break;
      case 'Tab':
        handled = step(pane, event.shiftKey ? -1 : 1);
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        var card = pane.detail.querySelector('[data-pcard]');
        if (card && card.__step) {
          card.__step(event.key === 'ArrowDown' ? 1 : -1);
          handled = true;
        }
        break;
      }
      case ' ':
      case 'Spacebar':
        if (!event.repeat && pane.currentHandle) {
          toggleFavorite(pane, pane.currentHandle);
          handled = true;
        }
        break;
      case 's':
      case 'S': {
        var sizeTrigger = pane.detail.querySelector('[data-size-request]');
        if (sizeTrigger && openSizeRequest(sizeTrigger)) handled = true;
        break;
      }
      case 'PageDown':
        goToPage(pane, deck.page + 1);
        handled = true;
        break;
      case 'PageUp':
        goToPage(pane, deck.page - 1);
        handled = true;
        break;
      default:
        break;
    }

    if (handled) event.preventDefault();
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  function init() {
    app = document.querySelector('[data-browse-app]');
    if (!app) return;

    collectionHandle = app.getAttribute('data-collection') || 'all';
    deck.perPage = parseInt(app.getAttribute('data-per-page'), 10) || 25;

    Array.prototype.forEach.call(app.querySelectorAll('[data-split-shell]'), function (shell) {
      var pane = new Pane(shell);
      panes[pane.key] = pane;
    });

    bindEvents();
    syncFavCount();

    /* The header is a separate section and must not reach into this closure,
       so it gets a narrow public surface instead. */
    /* The filter drawer is a separate file and must not reach into this
       closure, so it gets a narrow surface: the facets with their counts, the
       current selection, and one way to change it. */
    window.BrowseApp = {
      setFilters: setFilters,
      showTab: showTab,
      ready: function () { return Catalog.ready && !Catalog.failed; },
      filters: function () {
        var copy = {};
        for (var key in deck.filters) {
          if (Object.prototype.hasOwnProperty.call(deck.filters, key)) {
            copy[key] = deck.filters[key].slice();
          }
        }
        return copy;
      },
      facets: function (state) { return Facets.groups(state || deck.filters); },
      resultCount: function (state) { return Facets.filter(state || deck.filters).length; },
      favorites: Favorites
    };

    refreshCartCount();

    var saved = null;
    try { saved = window.localStorage.getItem(KEYS.activeTab); } catch (error) {}
    showTab(panes[saved] ? saved : 'browse', { skipSave: true });

    /* One request for the whole catalogue, then everything renders from it. */
    /* Read before the catalogue loads, so the query string is cleaned off the
       URL on the first tick rather than after a network round trip. */
    /* Two things can seed the deck, and both are read before the catalogue so
       nothing renders on page one and then jumps.

       The stash wins over the address. A size request navigates away and comes
       back on a URL Shopify built rather than one this app wrote, so on that
       trip the stash knows more than the query string does. */
    var resume = resumeAfterSizeRequest();
    var linked = readUrlState();
    var start = resume || linked;

    if (resume && resume.tab && panes[resume.tab]) activeTab = resume.tab;
    if (start.filters) deck.filters = start.filters;

    Catalog.load().then(function () {
      var pane = panes[activeTab];
      if (!pane) return;

      /* Sending a size request costs a page load - the form has to post the
         ordinary way (see prepareSizeRequest) - so the deck is put back the way
         it was left: same tab, same filters, same page, same pair open, and the
         thank-you showing. A shared link is restored the same way, from the
         query string instead of the stash. */
      if (start.page > 1) deck.page = start.page;

      /* Suppressed so the deck does not fetch the first tile's card and then
         throw it away for the pair that was actually asked for. */
      loadPane(pane, { open: !start.handle });
      if (start.handle) openProduct(pane, start.handle);

      /* Only now. The address has been read, so writing it back can no longer
         erase what it was carrying. */
      urlSyncOn = true;
      syncUrl();

      if (resume) showSizeRequestResult(resume.ok);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
